/*
 * ===============================================================================================================
 *                                Copyright 2024, Blue Yonder Group, Inc.
 *                                           All Rights Reserved
 *
 *                               THIS IS UNPUBLISHED PROPRIETARY SOURCE CODE OF
 *                                          BLUE YONDER GROUP, INC.
 *
 *
 *                         The copyright notice above does not evidence any actual
 *                                 or intended publication of such source code.
 *
 * ===============================================================================================================
 */
import { useState, useReducer, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import _ from 'lodash';
import {
  Box,
  Button,
  IconButton,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Alert
} from '@mui/material';
import { LuiSelect, LuiToolbar } from '@jda/lui-common-component-library-mui5';
import { Trash } from '@jda/lui-common-icon-library-mui5';
import { Location, ILocationType } from '../../models/Locations/Locations';
import ManageLocationsDialogShell from './ManageLocationsDialogShell';
import LocationSelect from './LocationSelect';
import { ADD, DELETE, DROPDOWN_MAX_HEIGHT, ADD_RESTRICTION, REMOVE_RESTRICTION, LOCATIONS_CHANGED } from './constants';
import { useStyles } from './styles';
import { selectLocationTypes } from '../../store/locations/locationTypesSelector';
import { displaySnackbar } from '../../store/snackBar/snackBarActionCreator';
import interpolateString from '../../utility/stringInterpolation';

const isBrowser = !navigator.userAgent.includes('jsdom');

interface MaxLocationNumberAlertProps {
  maxNumber: number;
  message: string;
}

export function MaxLocationNumberAlert({ maxNumber, message }: MaxLocationNumberAlertProps) {
  const [isOpen, setIsOpen] = useState(true);

  const { classes } = useStyles();

  if (!isOpen) {
    return null;
  }

  const maxNumberStringified = `${maxNumber}`;

  return (
    <Alert
      severity="info"
      variant="outlined"
      onClose={() => {
        setIsOpen(false);
      }}
      className={classes.alertMessage}
    >
      {interpolateString([maxNumberStringified, maxNumberStringified], message)}
    </Alert>
  );
}

function tableRowReducer(
  state: {
    rows: { key: number; locationType?: string; locations: Location[] }[];
    nextKey: number;
  },
  action:
    | { type: 'addRow' }
    | { type: 'selectLocationType'; key: number; locationType: string }
    | { type: 'selectLocations'; key: number; locations: Location[] }
    | { type: 'deleteRow'; key: number }
) {
  switch (action.type) {
    case 'addRow':
      return {
        ...state,
        rows: [...state.rows, { key: state.nextKey, locations: [] }],
        nextKey: state.nextKey + 1
      };
    case 'selectLocationType': {
      const rowIndex = state.rows.findIndex(({ key }) => key === action.key);

      return {
        ...state,
        rows: [
          ...state.rows.slice(0, rowIndex),
          {
            ...state.rows[rowIndex],
            locationType: action.locationType
          },
          ...state.rows.slice(rowIndex + 1)
        ]
      };
    }
    case 'selectLocations': {
      const rowIndex = state.rows.findIndex(({ key }) => key === action.key);

      return {
        ...state,
        rows: [
          ...state.rows.slice(0, rowIndex),
          {
            ...state.rows[rowIndex],
            locations: action.locations
          },
          ...state.rows.slice(rowIndex + 1)
        ]
      };
    }
    case 'deleteRow': {
      const deletingRowIndex = state.rows.findIndex(({ key }) => key === action.key);

      return {
        ...state,
        rows: [...state.rows.slice(0, deletingRowIndex), ...state.rows.slice(deletingRowIndex + 1)]
      };
    }
  }
}

export interface ManageLocationsDialogContentProps {
  currentLocations: Location[];
  maxToAdd?: number;
  maxToRemove?: number;
  disableCurrentLocations?: boolean;
  isLocationDisabled?: (location: Location) => boolean;
  onSave: (toAdd: Location[], toRemove: Location[]) => Promise<unknown>;
  onClose: () => void;
}

const ManageLocationsDialogContent = ({
  currentLocations,
  maxToAdd,
  maxToRemove,
  disableCurrentLocations = false,
  isLocationDisabled,
  onSave,
  onClose
}: ManageLocationsDialogContentProps) => {
  const locationTypes: ILocationType[] = useSelector(selectLocationTypes);

  const currentLocationsByType = useMemo(() => _.groupBy(currentLocations, location => location.locationType), [
    currentLocations
  ]);

  const [{ rows: tableRows }, dispatchTableRowAction] = useReducer(tableRowReducer, null, () => {
    const currentLocationsByTypeEntries = Object.entries(currentLocationsByType).filter(
      ([locationType]) => !isBrowser || locationType === 'PUBLIC_WAREHOUSE'
    );

    const rows =
      currentLocationsByTypeEntries.length > 0
        ? currentLocationsByTypeEntries.map(([locationType, locations], index) => ({
          key: index,
          locationType,
          locations
        }))
        : [{ key: 0, locations: [], ...(isBrowser ? { locationType: 'PUBLIC_WAREHOUSE' } : {}) }];

    return {
      rows,
      nextKey: rows.length
    };
  });
  const [isRequesting, setIsRequesting] = useState(false);

  const locationTypesData = useMemo(
    () =>
      locationTypes.map(locationType => ({ ...locationType, key: locationType.name, displayName: locationType.name })),
    [locationTypes]
  );

  const locationTypeOptions = useMemo(() => {
    const tableLocationTypeSet = new Set(tableRows.map(row => row.locationType));

    return locationTypesData.filter(({ name }: { name: string }) => !tableLocationTypeSet.has(name));
  }, [tableRows, locationTypesData]);

  const { locationsToAdd, locationsToRemove } = useMemo(() => {
    const currentLocationSet = new Set(currentLocations.map(location => location.id));

    const selectedLocations = tableRows.flatMap(row => row.locations);
    const selectedLocationSet = new Set(selectedLocations.map(location => location.id));

    return {
      locationsToAdd: selectedLocations.filter(location => !currentLocationSet.has(location.id)),
      locationsToRemove: currentLocations.filter(location => !selectedLocationSet.has(location.id))
    };
  }, [currentLocations, tableRows]);

  const dispatch = useDispatch();
  const { classes } = useStyles();

  const isTooManyLocationsToAdd = maxToAdd !== undefined && locationsToAdd.length > maxToAdd;
  const isTooManyLocationsToRemove = maxToRemove !== undefined && locationsToRemove.length > maxToRemove;

  const handleSave = async () => {
    setIsRequesting(true);
    try {
      await onSave(locationsToAdd, locationsToRemove);
      onClose();
      dispatch(displaySnackbar(LOCATIONS_CHANGED, 'success'));
    } catch (error: any) {
      setIsRequesting(false);
      dispatch(displaySnackbar(error.response?.data?.message || error.message, 'error'));
    }
  };

  return (
    <ManageLocationsDialogShell
      isRequesting={isRequesting}
      isSaveDisabled={
        (locationsToAdd.length === 0 && locationsToRemove.length === 0) ||
        isTooManyLocationsToAdd ||
        isTooManyLocationsToRemove
      }
      onSave={handleSave}
      onClose={onClose}
    >
      {!isBrowser && (
        <LuiToolbar
          rightZone={{
            component: (
              <Button
                variant="outlined"
                color="primary"
                disabled={tableRows.length >= locationTypesData.length}
                onClick={() => {
                  dispatchTableRowAction({ type: 'addRow' });
                }}
              >
                {ADD}
              </Button>
            )
          }}
          className={classes.toolbar}
        />
      )}
      <Box className={classes.manageLocations}>
        {isTooManyLocationsToAdd && <MaxLocationNumberAlert maxNumber={maxToAdd} message={ADD_RESTRICTION} />}
        {isTooManyLocationsToRemove && <MaxLocationNumberAlert maxNumber={maxToRemove} message={REMOVE_RESTRICTION} />}
        <TableContainer>
          <Table aria-label="simple table">
            <TableHead>
              <TableRow>
                <TableCell style={{ width: '35%' }}>Sub-Realm Type</TableCell>
                <TableCell style={{ width: '60%' }}>Sub-Realms</TableCell>
                {!isBrowser && <TableCell style={{ width: '5%' }} />}
              </TableRow>
            </TableHead>
            <TableBody>
              {tableRows.map(({ key, locationType, locations }) => {
                const selectedLocationType = locationTypesData.find(
                  ({ name }: { name: string }) => name === locationType
                );

                return (
                  <TableRow key={key}>
                    <TableCell>
                      {(!locationType || locations.length === 0) && !isBrowser ? (
                        <LuiSelect
                          maxHeight={DROPDOWN_MAX_HEIGHT}
                          id={`location-types-dropdown-${key}`}
                          itemData={
                            selectedLocationType ? [selectedLocationType, ...locationTypeOptions] : locationTypeOptions
                          }
                          inputLabel={''}
                          onSelectionChange={event => {
                            dispatchTableRowAction({
                              type: 'selectLocationType',
                              key,
                              locationType: event.target.value.key
                            });
                          }}
                          selectedItem={selectedLocationType}
                        />
                      ) : (
                        <TextField value={locationType} InputProps={{ readOnly: true }} fullWidth={true} />
                      )}
                    </TableCell>
                    <TableCell>
                      <LocationSelect
                        id={`location-multiple-autocomplete-${key}`}
                        locationType={locationType}
                        value={locations}
                        disabledLocationIds={
                          (disableCurrentLocations &&
                            locationType &&
                            currentLocationsByType[locationType]?.map(location => location.id)) ||
                          []
                        }
                        isLocationDisabled={isLocationDisabled}
                        onChange={locations => {
                          dispatchTableRowAction({ type: 'selectLocations', key, locations });
                        }}
                      />
                    </TableCell>
                    {!isBrowser && (
                      <TableCell>
                        <IconButton
                          aria-label={DELETE}
                          size="small"
                          onClick={() => {
                            dispatchTableRowAction({
                              type: 'deleteRow',
                              key
                            });
                          }}
                        >
                          <Trash />
                        </IconButton>
                      </TableCell>
                    )}
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>
    </ManageLocationsDialogShell>
  );
};

export default ManageLocationsDialogContent;
